import { api } from "@/api";
import { Asset, CompanyCreate, MinerUpdate, WithdrawCreate } from "@/interfaces";
import router from "@/router";
import { getLocalToken, removeLocalToken, saveLocalToken } from "@/utils";
import { AxiosError } from "axios";
import { getStoreAccessors } from "typesafe-vuex";
import { ActionContext } from "vuex";
import { State } from "../state";
import { commitSetCompanies, commitSetImage, commitSetIncomes, commitSetWithdrawStates } from "../main/mutations";
import {
  commitAddNotification,
  commitRemoveNotification,
  commitSetLoggedIn,
  commitSetLogInError,
  commitSetToken,
  commitSetMobile,
  commitSetTokenSent,
  commitSetUserProfile,
  commitSetMiners,
  commitSetMiner,
  commitSetShares,
  commitSetMinerShares,
  commitSetCalling,
  commitSetWithdraws,
  commitSetWithdraw,
  commitSetLicenceAccepted
} from "./mutations";
import { AppNotification, MainState } from "./state";
import { commitSetUser } from "../admin/mutations";

type MainContext = ActionContext<MainState, State>;

export const actions = {
  async actionSendToken(context: MainContext, payload: { mobile: string }) {
    try {
      const response = await api.logInSendSMSToken(payload.mobile);
      const result = response.data.result;
      if (result) {
        // saveLocalToken(token);
        // commitSetToken(context, token);
        commitSetTokenSent(context, true);
        commitSetMobile(context, payload.mobile);
        commitSetLogInError(context, false);
        // await dispatchGetUserProfile(context);
        await dispatchRouteTokenSent(context);
        commitAddNotification(context, {
          content: "کد امنیتی ارسال شد",
          color: "success"
        });
      } else {
        await dispatchLogOut(context);
      }
    } catch (err) {
      commitSetLogInError(context, true);
      await dispatchLogOut(context);
    }
  },
  async actionLogIn(
    context: MainContext,
    payload: { username: string; password: string }
  ) {
    try {

      commitSetLogInError(context, false);

      const response = await api.logInGetToken(
        payload.username,
        payload.password
      );

      const token = response.data.access_token;
      if (token) {
        saveLocalToken(token);
        commitSetToken(context, token);
        commitSetTokenSent(context, false);
        commitSetLoggedIn(context, true);
        commitSetLogInError(context, false);
        await dispatchGetUserProfile(context);
        await dispatchRouteLoggedIn(context);
        // commitAddNotification(context, {
        //   content: "خوش آمدید!",
        //   color: "success"
        // });
      } else {
        await dispatchLogOut(context);
      }
    } catch (err) {
      commitSetLogInError(context, true);
      await dispatchLogOut(context);
    }
  },
  async actionLogInWithSMS(
    context: MainContext,
    payload: { mobile: string; token: string }
  ) {
    try {

      commitSetLogInError(context, false);

      commitSetCalling(context, true);
      const response = await api.logInGetTokenWithSMS(
        payload.mobile,
        payload.token
      );

      commitSetCalling(context, false);
      const token = response.data.access_token;
      if (token) {
        saveLocalToken(token);
        commitSetToken(context, token);
        commitSetTokenSent(context, false);
        commitSetLoggedIn(context, true);
        commitSetLogInError(context, false);
        await dispatchGetUserProfile(context);
        await dispatchRouteLoggedIn(context);
        // commitAddNotification(context, {
        //   content: "خوش آمدید!",
        //   color: "success"
        // });
      } else {
        await dispatchLogOut(context);
      }
    } catch (err) {
      commitSetCalling(context, false);
      commitSetLogInError(context, true);
      await dispatchLogOut(context);
    }
  },
  async actionGetUserProfile(context: MainContext) {
    try {
      const response = await api.getMe(context.state.token);
      if (response.data) {
        commitSetUserProfile(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdateUserProfile(context: MainContext, payload) {
    try {
      const loadingNotification = {
        content: "در حال ذخیره سازی",
        showProgress: true
      };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateMe(context.state.token, payload),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitSetUserProfile(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "پروفایل کاربری با موفقیت ویرایش شد",
        color: "success"
      });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdateMiner(context: MainContext, payload) {
    try {
      const loadingNotification = {
        content: "در حال ذخیره سازی",
        showProgress: true
      };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateMiner(context.state.token, payload.id, payload.miner),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitSetMiner(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "اطلاعات ماینر با موفقیت ویرایش شد",
        color: "success"
      });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetMiner(context: MainContext, payload) {
    try {
      const loadingNotification = {
        content: "در حال دریافت اطلاعات",
        showProgress: true
      };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.getMiner(context.state.token, payload.id, true),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitSetMiner(context, response.data);
      if (response.data.shares) {
        commitSetShares(context, response.data.shares);
      }
      commitRemoveNotification(context, loadingNotification);
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionUpdateWithdraw(context: MainContext, payload) {
    try {
      const loadingNotification = {
        content: "در حال ذخیره سازی",
        showProgress: true
      };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.updateWithdraw(context.state.token, payload.id, payload.withdraw),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitSetWithdraw(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "اطلاعات برداشت با موفقیت ویرایش شد",
        color: "success"
      });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionSettleWithdraw(context: MainContext, payload) {
    try {
      const loadingNotification = {
        content: "در حال تسویه تراکنش",
        showProgress: true
      };
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.settleWithdraw(
            context.state.token,
            payload.id,
            payload.withdrawSettle
          ),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitSetWithdraw(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "درخواست برداشت با موفقیت تسویه شد",
        color: "success"
      });
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionRevertWithdraw(context: MainContext, payload) {
    const loadingNotification = {
      content: "در حال حذف درخواست",
      showProgress: true
    };
    try {
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.revertWithdraw(context.state.token, payload.id),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitSetWithdraw(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "درخواست برداشت با موفقیت حذف شد",
        color: "success"
      });
    } catch (error) {
      const result = await dispatchCheckApiError(context, error);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: result,
        color: "error"
      });
    }
  },
  async actionConfirmWithdraw(context: MainContext, payload) {
    const loadingNotification = {
      content: "در حال تایید درخواست",
      showProgress: true
    };
    try {
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.confirmWithdraw(context.state.token, payload.id),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitSetWithdraw(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "درخواست برداشت با موفقیت تایید شد",
        color: "success"
      });
    } catch (error) {
      const result = await dispatchCheckApiError(context, error);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: result,
        color: "error"
      });
    }
  },
  async actionCheckLoggedIn(context: MainContext) {
    if (!context.state.isLoggedIn) {
      let token = context.state.token;
      if (!token) {
        const localToken = getLocalToken();
        if (localToken) {
          commitSetToken(context, localToken);
          token = localToken;
        }
      }
      if (token) {
        try {
          const response = await api.getMe(token);
          commitSetLoggedIn(context, true);
          commitSetUserProfile(context, response.data);
        } catch (error) {
          await dispatchRemoveLogIn(context);
        }
      } else {
        await dispatchRemoveLogIn(context);
      }
    }
  },
  async actionRemoveLogIn(context: MainContext) {
    removeLocalToken();
    commitSetToken(context, "");
    commitSetLoggedIn(context, false);
  },
  async actionLogOut(context: MainContext) {
    await dispatchRemoveLogIn(context);
    await dispatchRouteLogOut(context);
  },
  async actionUserLogOut(context: MainContext) {
    await dispatchLogOut(context);
    commitAddNotification(context, {
      content: "شما با موفقیت خارج شدید",
      color: "success"
    });
  },
  actionRouteLogOut(context: MainContext) {
    if (router.currentRoute.path !== "/login") {
      router.push("/token");
    }
  },
  actionRouteLogin(context: MainContext) {
    router.push("/token");
  },
  async actionCheckApiError(context: MainContext, payload: AxiosError) {
    if (payload.response!.status === 401 || payload.response!.status === 403) {
      await dispatchLogOut(context);
    }
    if (payload.response!.status === 406) {
      return "داده دریافتی قابل قبول نیست";
    }
    return payload.message;
  },
  actionRouteTokenSent(context: MainContext) {
    if (
      router.currentRoute.path === "/token" ||
      router.currentRoute.path === "/"
    ) {
      router.push("/login");
    }
  },
  actionRouteLoggedIn(context: MainContext) {
    if (
      router.currentRoute.path === "/login" ||
      router.currentRoute.path === "/" ||
      router.currentRoute.path === "/token"
    ) {
      router.push("/main");
    }
  },
  async removeNotification(
    context: MainContext,
    payload: { notification: AppNotification; timeout: number }
  ) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        commitRemoveNotification(context, payload.notification);
        resolve(true);
      }, payload.timeout);
    });
  },
  async passwordRecovery(context: MainContext, payload: { username: string }) {
    const loadingNotification = {
      content: "در حال ارسال ایمیل بازیابی پسورد",
      showProgress: true
    };
    try {
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.passwordRecovery(payload.username),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "ایمیل بازیابی پسورد با موفقیت ارسال شد",
        color: "success"
      });
      await dispatchLogOut(context);
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        color: "error",
        content: "نام کاربری یا کلمه عبور غلط است"
      });
    }
  },
  async resetPassword(
    context: MainContext,
    payload: { password: string; token: string }
  ) {
    const loadingNotification = {
      content: "بازنشانی کلمه عبور",
      showProgress: true
    };
    try {
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.resetPassword(payload.password, payload.token),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "کلمه عبور با موفقیت بازنشانی شد",
        color: "success"
      });
      await dispatchLogOut(context);
    } catch (error) {
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        color: "error",
        content: "خطا در بازنشانی کلمه عبور"
      });
    }
  },
  async actionGetMiners(context: MainContext) {
    try {
      const response = await api.getMiners(context.rootState.main.token);
      if (response) {
        commitSetMiners(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetWithdraws(context: MainContext) {
    try {
      const response = await api.getWithdraws(context.rootState.main.token);
      if (response) {
        commitSetWithdraws(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetWithdrawReciptImage(
    context: MainContext,
    payload: { withdrawId: number }
  ) {
    try {
      const response = await api.getRecieptImage(
        context.rootState.main.token,
        payload.withdrawId
      );
      if (response) {
        commitSetImage(context, response.data);
      }
    } catch (error) {
      commitSetImage(context, null);
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetWithdrawStates(context: MainContext) {
    try {
      const response = await api.getWithdrawStates();
      if (response) {
        commitSetWithdrawStates(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetShares(context: MainContext) {
    try {
      const response = await api.getShares(context.rootState.main.token);
      if (response) {
        commitSetShares(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionGetSharesByMiner(
    context: MainContext,
    payload: { minerId: number; limit: number; offset: number }
  ) {
    try {
      const response = await api.getSharesByMinerId(
        context.rootState.main.token,
        payload.minerId,
        payload.limit,
        payload.offset
      );
      if (response) {
        commitSetMinerShares(context, {
          minerId: payload.minerId,
          shares: response.data
        });
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },
  async actionCreateWithdraw(context: MainContext, payload: WithdrawCreate) {
    const loadingNotification = {
      content: "در حال ذخیره سازی",
      showProgress: true
    };
    try {
      commitAddNotification(context, loadingNotification);
      const response = (
        await Promise.all([
          api.createWithdraw(context.rootState.main.token, payload),
          await new Promise((resolve, reject) =>
            setTimeout(() => resolve(), 500)
          )
        ])
      )[0];
      commitSetWithdraw(context, response.data);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: "درخواست برداشت با موفقیت ثبت شد.",
        color: "success"
      });
      return response.data;
    } catch (error) {
      const response = await dispatchCheckApiError(context, error);
      commitRemoveNotification(context, loadingNotification);
      commitAddNotification(context, {
        content: response,
        color: "error"
      });
      return null;
    }
  },
  async actionGetIncomes(context: MainContext) {
    try {
      const response = await api.getIncomes(context.rootState.main.token);
      if (response) {
        commitSetIncomes(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  }, async actionCreateCompany(context: MainContext, payload :{ data: CompanyCreate, own: boolean}) {
    try {
      const response = await api.createCompany(context.rootState.main.token, payload.data);
      if (response) {
        await dispatchGetCompanies(context,payload.own);
        return true
      }
    } catch (error) {
      if (error.response!.status === 400)
        return "ماینر(های) انتخاب شده در یک کمپانی دیگر ثبت شده است"
      else
        await dispatchCheckApiError(context, error);
    }
  }, async actionGetCompanies(context: MainContext, own: boolean) {
    try {
      const response = await api.getCompanies(context.rootState.main.token, own);
      if (response) {
        commitSetCompanies(context, response.data);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  }, async actionGetCompany(context: MainContext, payload: { own: boolean, id: number }) {
    try {

      const response = await api.getCompany(context.rootState.main.token, payload.own, payload.id);
      if (response) {
        commitSetCompanies(context, [...response.data]);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  }, async actionDeleteCompany(context: MainContext, payload: { own: boolean, id: number }) {
    try {
      const response = await api.deleteCompany(context.rootState.main.token, payload.own, payload.id);
      if (response) {
        await dispatchGetCompanies(context,payload.own);
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  }, async actionGetLicence(context: MainContext) {
    try {
      const response = await api.getLicence(context.rootState.main.token);
      if (response) {
        return response.data;
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  }, async actionGetAgreedLicenceVersion(context: MainContext) {
    try {
      const response = await api.getAsset(context.rootState.main.token, "agreed_license_version");
      if (response) {
        return response.data;
      }
    } catch (error) {
      if (error.response!.status === 404){
        return {id:-1,data:{version:""}}
      }else
        await dispatchCheckApiError(context, error);
    }
  }, async actionSetAgreedLicenceVersion(context: MainContext, version: string) {
    try {
      const asset: Asset = {
        name: "agreed_license_version",
        data: { version: version },
        description: ""
      };
      const userId = context.rootState.main.userProfile?.id ? context.rootState.main.userProfile?.id : -1;
      const response = await api.createAsset(context.rootState.main.token, userId, false, asset);
      if (response) {
        return response.data;
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  }, async actionSetLicenceAccepted(context: MainContext, isAccepted: boolean) {
    await commitSetLicenceAccepted(context, isAccepted);
  }, async actionUpdateAgreedLicenceVersion(context: MainContext, payload: { id: number, version: string }) {
    try {
      const asset: Asset = {
        name: "agreed_license_version",
        data: { version: payload.version },
        description: ""
      };
      const userId = context.rootState.main.userProfile?.id ? context.rootState.main.userProfile?.id : -1;
      const response = await api.updateAsset(context.rootState.main.token, payload.id, asset);
      if (response) {
        return response.data;
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },async actionAddReferral(context: MainContext, referral) {
    try {
      const response = await api.addReferral(context.rootState.main.token, referral);
      if (response) {
        return true;
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  },async actionGetAppInfo(context: MainContext) {
    try {
      const response = await api.getAppInfo(context.rootState.main.token);
      if (response) {
        return response.data;
      }
    } catch (error) {
      await dispatchCheckApiError(context, error);
    }
  }
};

const { dispatch } = getStoreAccessors<MainState | any, State>("");

export const dispatchCheckApiError = dispatch(actions.actionCheckApiError);
export const dispatchCheckLoggedIn = dispatch(actions.actionCheckLoggedIn);
export const dispatchGetUserProfile = dispatch(actions.actionGetUserProfile);
export const dispatchSendToken = dispatch(actions.actionSendToken);
export const dispatchLogIn = dispatch(actions.actionLogIn);
export const dispatchLogInSMS = dispatch(actions.actionLogInWithSMS);
export const dispatchLogOut = dispatch(actions.actionLogOut);
export const dispatchUserLogOut = dispatch(actions.actionUserLogOut);
export const dispatchRemoveLogIn = dispatch(actions.actionRemoveLogIn);
export const dispatchRouteTokenSent = dispatch(actions.actionRouteTokenSent);
export const dispatchRouteLoggedIn = dispatch(actions.actionRouteLoggedIn);
export const dispatchRouteLogOut = dispatch(actions.actionRouteLogOut);
export const dispatchRouteLogin = dispatch(actions.actionRouteLogin);
export const dispatchUpdateUserProfile = dispatch(
  actions.actionUpdateUserProfile
);

export const dispatchGetMiners = dispatch(actions.actionGetMiners);
export const dispatchGetMiner = dispatch(actions.actionGetMiner);
export const dispatchUpdateMiner = dispatch(actions.actionUpdateMiner);

export const dispatchGetWithdraws = dispatch(actions.actionGetWithdraws);
export const dispatchGetWithdrawStates = dispatch(
  actions.actionGetWithdrawStates
);

export const dispatchCreateWithdraw = dispatch(actions.actionCreateWithdraw);
export const dispatchUpdateWithdraw = dispatch(actions.actionUpdateWithdraw);
export const dispatchConfirmWithdraw = dispatch(actions.actionConfirmWithdraw);
export const dispatchSettleWithdraw = dispatch(actions.actionSettleWithdraw);
export const dispatchRevertWithdraw = dispatch(actions.actionRevertWithdraw);
export const dispatchGetWithdrawRecipetImage = dispatch(
  actions.actionGetWithdrawReciptImage
);

export const dispatchRemoveNotification = dispatch(actions.removeNotification);
export const dispatchPasswordRecovery = dispatch(actions.passwordRecovery);
export const dispatchResetPassword = dispatch(actions.resetPassword);

export const dispatchGetShares = dispatch(actions.actionGetShares);
export const dispatchGetSharesByMiner = dispatch(
  actions.actionGetSharesByMiner
);

export const dispatchGetIncomes = dispatch(actions.actionGetIncomes);
export const dispatchCreateCompany = dispatch(actions.actionCreateCompany);
export const dispatchGetCompanies = dispatch(actions.actionGetCompanies);
export const dispatchGetCompany = dispatch(actions.actionGetCompany);
export const dispatchDeleteCompany = dispatch(actions.actionDeleteCompany);

export const dispatchGetLicence = dispatch(actions.actionGetLicence);
export const dispatchGetAgreedLicenceVersion = dispatch(actions.actionGetAgreedLicenceVersion);
export const dispatchSetAgreedLicenceVersion = dispatch(actions.actionSetAgreedLicenceVersion);
export const dispatchUpdateAgreedLicenceVersion = dispatch(actions.actionUpdateAgreedLicenceVersion);
export const dispatchSetLicenceAccepted = dispatch(actions.actionSetLicenceAccepted);
export const dispatchAddReferral = dispatch(actions.actionAddReferral);
export const dispatchGetAppInfo = dispatch(actions.actionGetAppInfo);

